/**
 * App-specific session state for the SourcePilot RFLP / PDM frontend.
 *
 * Anything typed with PDM-domain models (Product, PDMAction,
 * FunctionalSubsystem, FunctionalModule) lives here rather than in
 * `@sourcepilot/ui` — a second app under `frontend/apps/*` that doesn't
 * load the PDM module wouldn't have these generated classes, so ui can't
 * statically depend on them.
 *
 * Shell-generic session state (tabs, page loading, mini-app width,
 * patch state, etc.) lives in
 * `@sourcepilot/ui/framework/state/session-data`.
 *
 * Components in ui that need access to these atoms should consume them
 * via React context (see contexts/CurrentNavigationItemContext etc.) so
 * ui stays agnostic of the app's domain.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { sessionStorage } from '@sourcepilot/ui/framework/data/utils';
import { localUuid } from '@sourcepilot/framework/uuid';

import { PDMProduct as Product } from '@sourcepilot/generated/pdm/api/pdmproduct';
import { PDMAction } from '@sourcepilot/generated/pdm/api/pdmaction';
import { FunctionalModule } from '@sourcepilot/generated/pdm/api/functionalmodule';
import { FunctionalSubsystem } from '@sourcepilot/generated/pdm/api/functionalsubsystem';
import { WorkspaceSketch } from '@/types/workspace';

// ───────────────────────────────────────────────────────────────────────────
// Product mini-app state
// ───────────────────────────────────────────────────────────────────────────

export interface ProductMiniAppState {
  workspace: WorkspaceSketch | null;
  expandedItems: string[];
  chatMode: 'chat' | null;
}

export const productMiniAppStateAtom = atomWithStorage<ProductMiniAppState>(
  'productMiniAppState',
  {
    workspace: null,
    expandedItems: [],
    chatMode: null,
  },
  sessionStorage,
);

// ───────────────────────────────────────────────────────────────────────────
// Current navigation entity (loaded via DAG sync in layout, shared
// between layout and child pages)
// ───────────────────────────────────────────────────────────────────────────

export const currentProductAtom = atom<Product | null>(null);

// Bump to request an authoritative workspace refetch (products + RFLP
// projects). `SourcePilotAppHostProvider` watches this and re-runs
// `fetchWorkspace`. Used after a project delete so the sidebar reconciles
// with the server in addition to the optimistic local prune.
export const workspaceRefetchSignalAtom = atom(0);

// For accessing the version, to trigger re-renders.
export const productDAGSubscriptionAtom = atom<any>(null);

// Current module/subsystem (loaded in layout).
export const currentModuleAtom = atom<any | null>(null);

// Current action (reactively queried from product DAG).
export const currentActionAtom = atom<PDMAction | null>(null);

// Node state atom for SpecsContent component
export const currentNodeAtom = atom<Product | FunctionalSubsystem | FunctionalModule>(
  new Product({ id: localUuid(), name: '' }),
);
